import React, { ReactElement, CSSProperties, FocusEventHandler } from "react";
import ct from "class-types.macro";
import camelcase from "lodash/camelCase";
import ConnectForm from "./ConnectForm";
import InputBase from "./InputBase";
import { inputLabel, inputMessage } from "./inputStyles";
import InputError from "./InputError";
import { cls } from "@wss/cls";

export type InputProps = {
  label: string;
  name?: string;
  id?: string;
  placeholder?: string;
  type?: "text" | "tel" | "password" | "date" | "email" | "number" | "decimal";
  required?: boolean;
  showRequired?: boolean;
  showOptional?: boolean;
  displayErrorMessage?: boolean;
  hintMessage?: string;
  children?: ReactElement;
  defaultValues?: string;
  labelClassName?: string;
  labelExtra?: React.ReactNode;
  errorText?: string;
  labelStyle?: CSSProperties;
  style?: CSSProperties;
  className?: string;
  containerPadding?: boolean;
  errorPosition?: string;
  errorStyles?: CSSProperties;
  inputErrorStyles?: CSSProperties;
  overrideDefaultErrorPositioning?: boolean;
  ariaLabel?: string;
  maxLength?: number;
  "aria-describedby"?: string;
  labelExtraClassName?: string;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  min?: string;
  max?: string;
  clearErrorOnFocus?: boolean;
  errorClassName?: string;
  innerRef?: React.LegacyRef<HTMLInputElement>;
  overrideDefaultInputHeight?: boolean;
  dataTestId?: string;
  inputMode?:
    | "text"
    | "tel"
    | "email"
    | "numeric"
    | "none"
    | "url"
    | "decimal"
    | "search"
    | undefined;
  spellCheck?: boolean;
  containerClassName?: string;
  containerStyles?: CSSProperties;
  autoComplete?: string;
};

const Input = ({
  label,
  type = "text",
  name = camelcase(label),
  id = name,
  required = false,
  showRequired = true,
  showOptional = false,
  displayErrorMessage = true,
  hintMessage = "",
  defaultValues = "",
  labelClassName = inputLabel,
  labelExtra = null,
  errorText,
  children,
  className,
  labelStyle = {},
  errorPosition,
  errorStyles,
  inputErrorStyles = {},
  ariaLabel = "",
  labelExtraClassName,
  overrideDefaultErrorPositioning = false,
  clearErrorOnFocus = true,
  errorClassName = "",
  overrideDefaultInputHeight = false,
  inputMode = "text",
  spellCheck = true,
  containerClassName = "",
  containerStyles = {},
  autoComplete = "on",
  ...rest
}: InputProps) => {
  return (
    <ConnectForm>
      {({ errors, clearErrors }) => {
        const error = errors[name];

        return (
          <div
            className={cls("pb-6 relative", containerClassName)}
            style={containerStyles}
          >
            {label ? (
              <label htmlFor={id} className={labelClassName} style={labelStyle}>
                {label}
                {required && showRequired ? (
                  <span className={ct("text-red-500")}> *</span>
                ) : (
                  ""
                )}
                {!required && showOptional ? (
                  <span
                    className={ct("text-gray-700", "text-sm", "font-normal")}
                  >
                    {" "}
                    (Optional)
                  </span>
                ) : (
                  ""
                )}
                {labelExtra ? (
                  <span className={labelExtraClassName}> {labelExtra}</span>
                ) : (
                  ""
                )}
              </label>
            ) : null}
            <InputBase
              id={id}
              type={type}
              name={name}
              error={error}
              onFocus={() => {
                if (clearErrorOnFocus) {
                  clearErrors(name);
                }
              }}
              defaultValue={defaultValues}
              className={className}
              aria-label={ariaLabel}
              aria-required={required}
              {...(typeof error?.message === "string" &&
                error?.message.length > 0 && {
                  "aria-invalid": true,
                  "aria-describedby": `${name}Error`,
                })}
              overrideDefaultInputHeight={overrideDefaultInputHeight}
              spellCheck={spellCheck}
              inputMode={inputMode}
              inputErrorStyles={inputErrorStyles}
              autoComplete={autoComplete}
              {...rest}
            />
            {children}
            {(!displayErrorMessage || !error) && hintMessage !== "" && (
              <span className={ct("absolute", inputMessage)}>
                {hintMessage}
              </span>
            )}
            <InputError
              errors={errors}
              label={errorText ?? (label || "This field")}
              name={name}
              displayErrorMessage={displayErrorMessage}
              errorPosition={errorPosition}
              styles={errorStyles}
              overridePosition={overrideDefaultErrorPositioning}
              className={errorClassName}
            />
          </div>
        );
      }}
    </ConnectForm>
  );
};

export default Input;
